import { useMutation } from "@tanstack/react-query";
import { useState } from "react";

import { Button, Dialog, TextField } from "@/components/ui";
import { localShiftRepository } from "@/repositories/localShiftRepository";

const COPY: Record<"PAY_IN" | "PAY_OUT", { title: string; verb: string; hint: string }> = {
  PAY_IN: {
    title: "Cash in",
    verb: "Record cash in",
    hint: "Money added to the drawer that is not a sale — change brought from the safe, or a float top-up.",
  },
  PAY_OUT: {
    title: "Cash out",
    verb: "Record cash out",
    hint: "Money taken from the drawer that is not change — a delivery paid in cash, or a drop to the safe.",
  },
};

/**
 * Money into or out of the drawer.
 *
 * The reason field is required and cannot be skipped. A drawer that opens
 * without one is the commonest way cash leaves a shop unnoticed, and "petty
 * cash" written on a slip six hours later is not a control. This is also what
 * makes a variance explainable at the end of the day rather than an argument.
 */
export function CashMovementDialog({
  shiftId,
  movementType,
  onClose,
  onDone,
}: {
  shiftId: string;
  movementType: "PAY_IN" | "PAY_OUT";
  onClose: () => void;
  onDone: () => void;
}) {
  const copy = COPY[movementType];
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [drop, setDrop] = useState(false);

  const record = useMutation({
    mutationFn: () =>
      localShiftRepository.cash({
        shiftId,
        movementType: drop ? "DROP" : movementType,
        amount: Number(amount).toFixed(2),
        reason: reason.trim(),
      }),
    onSuccess: onDone,
  });

  const parsed = Number(amount);
  const valid = Number.isFinite(parsed) && parsed > 0 && reason.trim().length > 0;

  return (
    <Dialog
      open
      onClose={onClose}
      size="sm"
      title={copy.title}
      description={copy.hint}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={record.isPending}
            disabled={!valid}
            onClick={() => record.mutate()}
          >
            {copy.verb}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <TextField
          label="Amount (R)"
          type="number"
          inputMode="decimal"
          step="0.01"
          min="0"
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
          hint="Always a positive amount. Whether it goes in or out is what you chose."
          autoFocus
        />

        <TextField
          label="What is it for?"
          value={reason}
          maxLength={200}
          onChange={(event) => setReason(event.target.value)}
          placeholder={
            movementType === "PAY_OUT" ? "Paid the bread delivery" : "Change from the safe"
          }
        />

        {movementType === "PAY_OUT" && (
          <label className="flex items-start gap-2.5 text-sm text-ink-600">
            <input
              type="checkbox"
              checked={drop}
              onChange={(event) => setDrop(event.target.checked)}
              className="mt-0.5 size-4 rounded border-line text-brand-500 focus:ring-brand-500/20"
            />
            <span>
              This is a <strong className="font-medium text-ink-900">drop to the safe</strong>, not
              a payment. Recorded separately so a shop can see how much cash it moved off the
              counter during the day.
            </span>
          </label>
        )}

        {record.error != null && (
          <p className="text-sm text-danger-700">
            {record.error instanceof Error ? record.error.message : "That did not work."}
          </p>
        )}
      </div>
    </Dialog>
  );
}
